import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "@/components/feature/Sidebar";
import ArticleCard from "@/components/feature/ArticleCard";
import { categoryMap } from "@/mocks/categories";
import { disclaimer } from "@/mocks/articles";
import { formatDate, getArticleById, getRelated } from "@/lib/content";

export default function DetailPage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const [activeId, setActiveId] = useState("");
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    if (!article) return;
    setActiveId(article.sections[0]?.id ?? "");
    setTocOpen(false);
  }, [article]);

  useEffect(() => {
    if (!article) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    article.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="text-2xl font-bold text-foreground-950">内容不存在或已下架</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的详情页不存在。可以返回首页，或按分类继续浏览相关内容。
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
          <Link
            to="/search"
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
          >
            前往检索
          </Link>
        </div>
      </div>
    );
  }

  const category = categoryMap[article.category];
  const related = getRelated(article, 3);

  const scrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 88, behavior: "smooth" });
    }
    setTocOpen(false);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-8 px-4 py-8 md:px-8 lg:flex-row">
      <Sidebar activeType={article.category} />

      <div className="min-w-0 flex-1">
        <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${article.category}`} className="transition-colors hover:text-primary-700">
            {category?.name}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="line-clamp-1 text-foreground-800">{article.title}</span>
        </nav>

        <article>
          <header className="mb-6">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <i className={category?.icon ?? "ri-article-line"} />
              {category?.name}
            </span>
            <h1 className="text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-link" />
                {article.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                约 {article.readTime} 分钟阅读
              </span>
            </div>
          </header>

          <div className="mb-6 overflow-hidden rounded-lg border border-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 封面`}
              title={`${article.title} - 乱伦专题内容`}
              className="h-56 w-full object-cover object-top md:h-80"
            />
          </div>

          <p className="mb-6 rounded-lg border border-background-200 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700">
            {article.summary}
          </p>

          {/* 移动端目录 */}
          <div className="mb-6 rounded-lg border border-background-200 bg-background-50 lg:hidden">
            <button
              type="button"
              onClick={() => setTocOpen((prev) => !prev)}
              className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-950"
            >
              <span className="flex items-center gap-2">
                <i className="ri-list-check-2 text-primary-600" />
                本文目录
              </span>
              <i className={tocOpen ? "ri-arrow-up-s-line text-lg" : "ri-arrow-down-s-line text-lg"} />
            </button>
            {tocOpen && (
              <ol className="animate-slideDown border-t border-background-200 px-4 py-3">
                {article.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => scrollTo(section.id)}
                      className="block w-full py-2 text-left text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      {section.heading}
                    </button>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div className="article-body min-w-0">
              {article.sections.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-24 mb-8">
                  <h2 className="mb-3 text-lg font-bold text-foreground-950 md:text-xl">{section.heading}</h2>
                  {section.paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}

              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-accent-800">
                  <i className="ri-shield-check-line" />
                  免责声明
                </h3>
                <p className="text-xs leading-relaxed text-accent-900/85">{disclaimer}</p>
              </div>
            </div>

            {/* 桌面端目录 */}
            <aside className="hidden lg:block">
              <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-600" />
                  本文目录
                </h3>
                <nav className="flex flex-col gap-1">
                  {article.sections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollTo(section.id)}
                      className={[
                        "border-l-2 py-1.5 pl-3 text-left text-xs leading-snug transition-colors",
                        activeId === section.id
                          ? "border-primary-600 font-medium text-primary-700"
                          : "border-background-200 text-foreground-500 hover:text-foreground-800",
                      ].join(" ")}
                    >
                      {section.heading}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          </div>
        </article>

        {/* 所属分类入口 */}
        {category && (
          <Link
            to={`/category/${article.category}`}
            className="mt-8 flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:bg-background-200/70"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
              <i className={`${category.icon} text-xl`} />
            </span>
            <div className="flex-1">
              <p className="text-xs text-foreground-500">返回所属分类</p>
              <p className="text-sm font-semibold text-foreground-950">{category.name}</p>
            </div>
            <i className="ri-arrow-right-line text-xl text-foreground-400" />
          </Link>
        )}

        {/* 相关内容推荐 */}
        {related.length > 0 && (
          <section className="mt-12">
            <div className="mb-5 flex items-center gap-2">
              <i className="ri-links-line text-xl text-accent-600" />
              <h2 className="text-lg font-bold text-foreground-950">相关内容推荐</h2>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}